import React from 'react';
import { useSelector } from 'react-redux';
import {
  BankTransactionWithParticipation,
  BankTransactionParticipation,
} from '../../../types/bankAccount';
import { RootState } from '../../../redux/reducers';
import { ConnectionType } from '../../../redux/slices/connectionsSlice';
import Avatar from '../misc/Avatar';
import useSelectedCommunity from '../../../hooks/community/useSelectedCommunity';

function AvatarList({ participants }: { participants: BankTransactionParticipation[] }) {
  const connectionsAll = useSelector((state: RootState) => state.connections);
  const connections = connectionsAll.filter((c) => c.type === ConnectionType.MEMBER);

  const displayParticipants = participants
    .map((p) =>
      connections.find((connection) => +connection.dashboardMemberId === +p.dashboardMemberId),
    )
    .filter(Boolean);

  return (
    <div className="flex items-center gap-[-8px]">
      {displayParticipants.length === 1 ? (
        <div className="flex items-center gap-2">
          <Avatar
            className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-900 text-xs font-bold text-secondary-50 shadow-md"
            src={displayParticipants[0]?.picture}
            alias={`${displayParticipants[0]?.fields.FIRST_NAME}+${displayParticipants[0]?.fields.LAST_NAME}`}
          />
          {displayParticipants[0]?.fields.FIRST_NAME} {displayParticipants[0]?.fields.LAST_NAME}
        </div>
      ) : (
        displayParticipants
          .slice(0, 2)
          .map((c, index) => (
            <Avatar
              key={`avatar_declarations_${index}_${c?.dashboardMemberId}`}
              className={`relative z-[${10 - index}] flex h-8 w-8 items-center justify-center rounded-full ${index === 0 ? 'bg-secondary-200' : index === 1 ? 'bg-primary-300' : 'bg-primary-900'} text-xs font-bold text-white shadow-md ${index === 0 ? '' : '-ml-2'}`}
              src={c?.picture}
              alias={`${c?.fields.FIRST_NAME}+${c?.fields.LAST_NAME}`}
            />
          ))
      )}
      {participants.length > 2 && (
        <div
          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-blue-900 text-sm font-bold text-white shadow-md"
          style={{
            marginLeft: '-8px',
            zIndex: 1,
          }}>
          +{participants.length - 2}
        </div>
      )}
    </div>
  );
}

export function PaidByInfo({
  declaration,
}: {
  declaration: BankTransactionWithParticipation;
}): JSX.Element | null {
  const connections = useSelector((state: RootState) => state.connections);
  const community = useSelectedCommunity();

  if (!community) return null;

  const payer = connections.find((x) => +x.dashboardMemberId === declaration.payerId);
  if (!payer) return null;

  const alias = `${payer.fields.FIRST_NAME}+${payer.fields.LAST_NAME}`;

  return (
    <div className="flex items-center gap-2">
      <Avatar className="h-8 w-8 rounded-full" src={payer?.picture} alias={alias} />
      <span>
        {payer.fields.FIRST_NAME} {payer.fields.LAST_NAME}
      </span>
    </div>
  );
}

export function PaidForInfo({
  declaration,
}: {
  declaration: BankTransactionWithParticipation;
}): JSX.Element | null {
  const community = useSelectedCommunity();

  if (!community) return null;

  if (!declaration.participations || declaration.participations.length === 0) {
    return (
      <div className="flex items-center gap-2">
        <Avatar className="h-8 w-8" src={community.picture ?? undefined} alias={community.name} />
        <span>{community.name}</span>
      </div>
    );
  }

  return <AvatarList participants={declaration.participations} />;
}
